import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Divider, InputAdornment,
    MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { ConfigCard } from '../style/cards';
import { CheckboxTile, PdiRow } from '../style/cards/_PdiHelpers';

// ────────────── Static option pools ──────────────
//
// These mirror the legacy admin's sync dropdowns 1:1 (values = the exact
// strings the storefront `settings` table stores). Dynamic pools (stores,
// category source labels) come from props.
const WEBSHOP_INSTANCES = [1, 2, 3, 4, 5].map(n => ({ value: String(n), label: `Online ${n}` }));
const LANG_OPTIONS      = [1, 2, 3].map(n => ({ value: String(n), label: `Lang ${n}` }));
const IMAGE_TYPE_OPTIONS = Array.from({ length: 10 }, (_, i) => {
    const code = String(i + 1).padStart(2, '0');
    return { value: code, label: `Type ${code}` };
});
// MAP_PRODUCT_NAME / MAP_PRODUCT_REFERENCE source columns (legacy enum).
const productFieldOptions = (tr) => [
    { value: 'id',           label: tr('cfg_sync_field_barbody', 'Barbody') },
    { value: 'detail1',      label: `${tr('cfg_sync_field_ref',  'Reference')} 1` },
    { value: 'detail2',      label: `${tr('cfg_sync_field_ref',  'Reference')} 2` },
    { value: 'detail3',      label: `${tr('cfg_sync_field_ref',  'Reference')} 3` },
    { value: 'detail4',      label: `${tr('cfg_sync_field_ref',  'Reference')} 4` },
    { value: 'detail5',      label: `${tr('cfg_sync_field_ref',  'Reference')} 5` },
    { value: 'description',  label: `${tr('cfg_sync_field_desc', 'Description')} 1` },
    { value: 'description2', label: `${tr('cfg_sync_field_desc', 'Description')} 2` },
    { value: 'description3', label: `${tr('cfg_sync_field_desc', 'Description')} 3` },
    { value: 'description4', label: `${tr('cfg_sync_field_desc', 'Description')} 4` },
    { value: 'description5', label: `${tr('cfg_sync_field_desc', 'Description')} 5` },
];

// Defaults mirror the storefront migrations (mig003_config / mig079 / mig151):
// identity category map, name=detail2 / ref=detail1, locales 1/2/3, photo
// types 01/02, square 100/300/800 + ratio heights 160/500/1300, white padding.
export const SYNC_CONFIG_DEFAULT = {
    // GENERAL
    sourceWebshop: '1',
    storesIncluded: [],            // expanded from FETCH_FOR_STORES ('00' = all) at load
    stockFiltering: {
        onlySyncWithStock:        false,
        useExternalStockProvider: false,
        showExternalOnlyProducts: false,
    },
    // FIELD MAPPING — 7 fixed levels, each reading from a source category code.
    categoryHierarchy: [1, 2, 3, 4, 5, 6, 7].map(n => ({ level: n, source: String(n).padStart(2, '0') })),
    productFields: { name: 'detail2', reference: 'detail1' },
    languageMapping: { nl: '1', en: '2', fr: '3' },
    // IMAGES
    imageSourceTypes: { primary: '01', alternative: '02' },
    imageProcessing: {
        useV2: false,
        sizes: {
            thumb:     { width: 100, height: 160  },
            preview:   { width: 300, height: 500  },
            processed: { width: 800, height: 1300 },
        },
        backgroundFillColor: '#ffffff',
    },
};

const SyncSettingsTab = ({ theme, t, subTab = 0, config, onConfigChange, warehouseOptions = [], shopFieldLabels = {} }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
    const c = config || SYNC_CONFIG_DEFAULT;

    // Controlled helpers — replace the whole config so the parent stays the
    // single source of truth (same pattern as the other config tabs).
    const setField   = (k, v) => onConfigChange({ ...c, [k]: v });
    const setSection = (k) => (v) => onConfigChange({ ...c, [k]: { ...(c[k] || {}), ...v } });

    // Source-category labels for the hierarchy dropdowns come from the shop's
    // kasvars (category_1..7); fall back to a generic "Category N".
    const categorySourceOptions = [1, 2, 3, 4, 5, 6, 7].map(n => ({
        value: String(n).padStart(2, '0'),
        label: (shopFieldLabels && shopFieldLabels[`category_${n}`]) || `${tr('cfg_sync_cat_default', 'Category')} ${n}`,
    }));

    return (
        <Box>
            {subTab === 0 && (
                <Stack spacing={2}>
                    <SourceWebshopCard
                        value={c.sourceWebshop}
                        onChange={(v) => setField('sourceWebshop', v)}
                        theme={theme} tr={tr}
                    />
                    <StoresIncludedCard
                        value={c.storesIncluded}
                        options={warehouseOptions}
                        onChange={(v) => setField('storesIncluded', v)}
                        theme={theme} tr={tr}
                    />
                    <StockFilteringCard
                        value={c.stockFiltering}
                        onChange={setSection('stockFiltering')}
                        theme={theme} tr={tr}
                    />
                </Stack>
            )}

            {subTab === 1 && (
                <Stack spacing={2}>
                    <CategoryHierarchyCard
                        value={c.categoryHierarchy}
                        options={categorySourceOptions}
                        onChange={(v) => setField('categoryHierarchy', v)}
                        theme={theme} tr={tr}
                    />
                    <ProductFieldsCard
                        value={c.productFields}
                        options={productFieldOptions(tr)}
                        onChange={setSection('productFields')}
                        theme={theme} tr={tr}
                    />
                    <LanguageMappingCard
                        value={c.languageMapping}
                        onChange={setSection('languageMapping')}
                        theme={theme} tr={tr}
                    />
                </Stack>
            )}

            {subTab === 2 && (
                <Stack spacing={2}>
                    <ImageSourceTypesCard
                        value={c.imageSourceTypes}
                        onChange={setSection('imageSourceTypes')}
                        theme={theme} tr={tr}
                    />
                    <ImageProcessingCard
                        value={c.imageProcessing}
                        onChange={setSection('imageProcessing')}
                        theme={theme} tr={tr}
                    />
                </Stack>
            )}
        </Box>
    );
};

SyncSettingsTab.propTypes = {
    theme: PropTypes.string,
    t: PropTypes.func,
    subTab: PropTypes.number,
    config: PropTypes.object,
    onConfigChange: PropTypes.func,
    warehouseOptions: PropTypes.array,
    shopFieldLabels: PropTypes.object,
};

// ─────────────────────────────────────────────────────────────────────────
// GENERAL — Source webshop  (FETCH_FOR_ONLINE)
// ─────────────────────────────────────────────────────────────────────────
const SourceWebshopCard = ({ value, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_source_title', 'Source webshop')}
        description={tr('cfg_sync_source_desc', 'Identifies which webshop instance this configuration pulls data for. The source application supports up to 5 webshops; pick which one this configuration represents.')}
        theme={theme}
    >
        <PdiRow
            label={tr('cfg_sync_source_instance', 'Webshop instance')}
            help={tr('cfg_sync_source_instance_help', 'Only products marked as online for this instance will be synced')}
            control={
                <TextField
                    select size="small"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    {WEBSHOP_INSTANCES.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </TextField>
            }
        />
    </ConfigCard>
);
SourceWebshopCard.propTypes = { value: PropTypes.string, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL — Stores included  (FETCH_FOR_STORES, '00' = all)
// ─────────────────────────────────────────────────────────────────────────
const StoresIncludedCard = ({ value = [], options = [], onChange, theme, tr }) => {
    const isSelected = (code) => value.includes(code);
    const toggle = (code) => onChange(isSelected(code) ? value.filter(x => x !== code) : [...value, code]);
    const selectAll = () => onChange(options.map(o => o.value));
    const clearAll  = () => onChange([]);
    // Labels arrive as "01 — Lochristi"; split the code chip off the name.
    const nameOf = (o) => {
        const parts = String(o.label).split(' — ');
        return parts.length > 1 ? parts.slice(1).join(' — ') : o.label;
    };

    return (
        <ConfigCard
            title={tr('cfg_sync_stores_title', 'Stores included')}
            description={tr('cfg_sync_stores_desc', 'Only products from these physical stores will be available in the webshop.')}
            theme={theme}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {value.length} {tr('cfg_sync_stores_of', 'of')} {options.length} {tr('cfg_sync_stores_selected', 'stores selected')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={selectAll} sx={{ textTransform: 'none' }}>
                        {tr('cfg_sync_stores_select_all', 'Select all')}
                    </Button>
                    <Button size="small" variant="outlined" onClick={clearAll} sx={{ textTransform: 'none' }}>
                        {tr('cfg_sync_stores_clear_all', 'Clear all')}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                    {options.map(o => (
                        <CheckboxTile
                            key={o.value}
                            label={
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem' }}>{o.value}</Typography>
                                    <Typography component="span" variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>{nameOf(o)}</Typography>
                                </Box>
                            }
                            checked={isSelected(o.value)}
                            onChange={() => toggle(o.value)}
                        />
                    ))}
                </Box>
            </Box>
        </ConfigCard>
    );
};
StoresIncludedCard.propTypes = { value: PropTypes.array, options: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL — Stock filtering
//   onlySyncWithStock        → FETCH_ONLY_WITH_STOCK
//   useExternalStockProvider → PRODUCT_USE_EXTERNAL_STOCK
//   showExternalOnlyProducts → OVERRULE_ONLY_IN_STOCK_FOR_EXT
// ─────────────────────────────────────────────────────────────────────────
const StockFilteringCard = ({ value = {}, onChange, theme, tr }) => {
    const set = (k) => (v) => onChange({ [k]: v });
    const externalOn = !!value.useExternalStockProvider;

    return (
        <ConfigCard
            title={tr('cfg_sync_stock_title', 'Stock filtering')}
            description={tr('cfg_sync_stock_desc', 'Control whether products without stock are synced to the webshop.')}
            theme={theme}
        >
            <Stack spacing={1.25} divider={<Divider />}>
                <PdiRow
                    label={tr('cfg_sync_stock_only', 'Only sync products with stock')}
                    help={tr('cfg_sync_stock_only_help', "Products with zero inventory in the selected stores won't appear in the webshop")}
                    control={
                        <Switch color="success" size="small"
                            checked={!!value.onlySyncWithStock}
                            onChange={(e) => set('onlySyncWithStock')(e.target.checked)}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_sync_stock_external', 'Use external stock provider')}
                    help={tr('cfg_sync_stock_external_help', "When local stock is empty, check an external supplier's inventory. Only products from supported brands are eligible.")}
                    control={
                        <Switch color="success" size="small"
                            checked={externalOn}
                            onChange={(e) => set('useExternalStockProvider')(e.target.checked)}
                        />
                    }
                />
                <Box sx={{ opacity: externalOn ? 1 : 0.5, pointerEvents: externalOn ? 'auto' : 'none' }}>
                    <PdiRow
                        indent
                        label={tr('cfg_sync_stock_external_only', 'Show external-only products')}
                        help={tr('cfg_sync_stock_external_only_help', "Override the 'only sync products with stock' rule above and include products that are only available externally. Useful for showing supplier inventory you can dropship.")}
                        control={
                            <Switch color="success" size="small"
                                checked={!!value.showExternalOnlyProducts}
                                onChange={(e) => set('showExternalOnlyProducts')(e.target.checked)}
                            />
                        }
                    />
                </Box>
            </Stack>
        </ConfigCard>
    );
};
StockFilteringCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// FIELD MAPPING — Category hierarchy  (MAP_CATEGORY_1..7)
//
// Seven fixed webshop category levels; each reads from one source category
// code (01-07). Defaults follow the source order (level 1 ← source 01, …).
// ─────────────────────────────────────────────────────────────────────────
const CategoryHierarchyCard = ({ value = [], options = [], onChange, theme, tr }) => {
    const setLevel = (level, source) => onChange(value.map(l => l.level === level ? { ...l, source } : l));

    return (
        <ConfigCard
            title={tr('cfg_sync_cat_title', 'Category hierarchy')}
            description={tr('cfg_sync_cat_desc', 'Define the category tree that drives navigation and filtering on the webshop. Each level reads from one source category, top to bottom.')}
            theme={theme}
        >
            <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                    {tr('cfg_sync_cat_helper', 'Order matters: level 1 is the highest category (e.g. gender), level 2 sits under it (e.g. type), and so on. The names come from the POS category labels.')}
                </Typography>

                <Stack spacing={0.75}>
                    {value.map((lvl) => (
                        <Box key={lvl.level} sx={{
                            display: 'grid',
                            gridTemplateColumns: '28px auto 1fr',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: '#fff',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1, py: 0.75,
                        }}>
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '50%',
                                backgroundColor: 'rgba(25,118,210,0.08)',
                                color: 'primary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '0.78rem',
                            }}>{lvl.level}</Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 80 }}>
                                {tr('cfg_sync_cat_level', 'Level')} {lvl.level}
                            </Typography>
                            <TextField
                                select size="small" fullWidth
                                value={lvl.source || ''}
                                onChange={(e) => setLevel(lvl.level, e.target.value)}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' } }}
                            >
                                {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </ConfigCard>
    );
};
CategoryHierarchyCard.propTypes = { value: PropTypes.array, options: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// FIELD MAPPING — Product fields  (MAP_PRODUCT_NAME / MAP_PRODUCT_REFERENCE)
// ─────────────────────────────────────────────────────────────────────────
const ProductFieldsCard = ({ value = {}, options = [], onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_pfields_title', 'Product fields')}
        description={tr('cfg_sync_pfields_desc', 'Pick which source fields hold the product name and reference (SKU).')}
        theme={theme}
    >
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <UpperLabelSelect
                    label={tr('cfg_sync_pfields_name', 'PRODUCT NAME')}
                    value={value.name || ''}
                    options={options}
                    onChange={(v) => onChange({ name: v })}
                />
                <UpperLabelSelect
                    label={tr('cfg_sync_pfields_ref', 'PRODUCT REFERENCE / SKU')}
                    value={value.reference || ''}
                    options={options}
                    onChange={(v) => onChange({ reference: v })}
                />
            </Box>
        </Box>
    </ConfigCard>
);
ProductFieldsCard.propTypes = { value: PropTypes.object, options: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// FIELD MAPPING — Language mapping  (MAP_LOCALE_NL / _EN / _FR)
// ─────────────────────────────────────────────────────────────────────────
const LanguageMappingCard = ({ value = {}, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_lang_title', 'Language mapping')}
        description={tr('cfg_sync_lang_desc', 'Match each shop language to the corresponding language slot in your source data. The source system stores translations in numbered slots (Lang 1, Lang 2, etc.).')}
        theme={theme}
    >
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                {[
                    { code: 'nl', short: 'NL', label: tr('cfg_sync_lang_nl', 'DUTCH READS FROM') },
                    { code: 'en', short: 'GB', label: tr('cfg_sync_lang_en', 'ENGLISH READS FROM') },
                    { code: 'fr', short: 'FR', label: tr('cfg_sync_lang_fr', 'FRENCH READS FROM') },
                ].map(lang => (
                    <Box key={lang.code}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                            <Box component="span" sx={{ color: 'text.disabled', mr: 0.5 }}>{lang.short}</Box>
                            {lang.label}
                        </Typography>
                        <TextField
                            select size="small" fullWidth
                            value={value[lang.code] || ''}
                            onChange={(e) => onChange({ [lang.code]: e.target.value })}
                            sx={{ backgroundColor: '#fff', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' } }}
                        >
                            {LANG_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                        </TextField>
                    </Box>
                ))}
            </Box>
        </Box>
    </ConfigCard>
);
LanguageMappingCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// IMAGES — Image source types  (PHOTO_MAIN_TYPE / PHOTO_SECOND_TYPE)
// ─────────────────────────────────────────────────────────────────────────
const ImageSourceTypesCard = ({ value = {}, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_imgsrc_title', 'Image source types')}
        description={tr('cfg_sync_imgsrc_desc', 'Identify which file type prefix in the source system represents which product image.')}
        theme={theme}
    >
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <UpperLabelSelect
                    label={tr('cfg_sync_imgsrc_primary', 'PRIMARY IMAGE (MAIN PHOTO)')}
                    value={value.primary || ''}
                    options={IMAGE_TYPE_OPTIONS}
                    onChange={(v) => onChange({ primary: v })}
                />
                <UpperLabelSelect
                    label={tr('cfg_sync_imgsrc_alt', 'ALTERNATIVE IMAGE (HOVER / SECONDARY)')}
                    value={value.alternative || ''}
                    options={IMAGE_TYPE_OPTIONS}
                    onChange={(v) => onChange({ alternative: v })}
                />
            </Box>
        </Box>
    </ConfigCard>
);
ImageSourceTypesCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// IMAGES — Image processing
//
// useV2                → USE_IMAGES_CRON_V2
// sizes.*.width        → PHOTO_SQUARE_SIZE_THUMB / _PREVIEW / _FULL   (always used)
// sizes.*.height       → PHOTO_RATIO_HEIGHT_THUMB / _PREVIEW / _FULL  (v2 only)
// backgroundFillColor  → PHOTO_PADDING_COLOR ("r,g,b")
//
// Legacy semantics: without v2 the widths are square sizes; with v2 they
// become independent max-width limits and the max-height limits appear too.
// ─────────────────────────────────────────────────────────────────────────
const ImageProcessingCard = ({ value = {}, onChange, theme, tr }) => {
    const v2 = !!value.useV2;
    const sizes = value.sizes || {};
    const setSize = (purpose, dim, n) => onChange({
        sizes: { ...sizes, [purpose]: { ...(sizes[purpose] || {}), [dim]: Math.max(1, parseInt(n) || 0) } },
    });

    const PURPOSES = [
        { key: 'thumb',     label: tr('cfg_sync_imgproc_thumb',      'Thumb'),
                            help:  tr('cfg_sync_imgproc_thumb_help', 'Small previews in lists and grids'),
                            defaults: { width: 100, height: 160 } },
        { key: 'preview',   label: tr('cfg_sync_imgproc_preview',     'Preview'),
                            help:  tr('cfg_sync_imgproc_preview_help','Medium-sized images on category pages'),
                            defaults: { width: 300, height: 500 } },
        { key: 'processed', label: tr('cfg_sync_imgproc_processed',     'Processed'),
                            help:  tr('cfg_sync_imgproc_processed_help','Full-size product detail page images'),
                            defaults: { width: 800, height: 1300 } },
    ];

    const widthHeader = v2 ? tr('cfg_sync_imgproc_max_w', 'MAXIMUM WIDTH') : tr('cfg_sync_imgproc_square', 'SQUARE SIZE');
    const gridCols = v2 ? '1fr 130px 130px' : '1fr 130px';

    return (
        <ConfigCard
            title={tr('cfg_sync_imgproc_title', 'Image processing')}
            description={tr('cfg_sync_imgproc_desc', 'When a new image is uploaded to the source, the system processes it into multiple sizes for the webshop.')}
            theme={theme}
        >
            <Alert severity="warning" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" sx={{ '& strong': { fontWeight: 700 } }}>
                    <Box component="strong">{tr('cfg_sync_imgproc_v2_rec_title', 'v2 processing recommended.')}</Box>{' '}
                    {tr('cfg_sync_imgproc_v2_rec_body', "The newer pipeline preserves aspect ratios and respects width/height limits independently, so images aren't forced into squares. Turn off only if you specifically need legacy behavior.")}
                </Typography>
            </Alert>

            <PdiRow
                label={tr('cfg_sync_imgproc_use_v2', 'Use v2 image processing')}
                help={tr('cfg_sync_imgproc_use_v2_help', 'Newer pipeline that respects aspect ratios and per-dimension limits')}
                control={
                    <Switch color="success" size="small"
                        checked={v2}
                        onChange={(e) => onChange({ useV2: e.target.checked })}
                    />
                }
            />

            <Box sx={{ pl: 2.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                    {v2 ? tr('cfg_sync_imgproc_max_sizes', 'Maximum sizes') : tr('cfg_sync_imgproc_square', 'Square size')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                    {tr('cfg_sync_imgproc_max_sizes_help', 'The longest a width or height can be for each image purpose. Smaller files load faster but show less detail.')}
                </Typography>

                <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            {tr('cfg_sync_imgproc_purpose', 'PURPOSE')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            {widthHeader}
                        </Typography>
                        {v2 && (
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                                {tr('cfg_sync_imgproc_max_h', 'MAXIMUM HEIGHT')}
                            </Typography>
                        )}
                    </Box>
                    <Stack spacing={0.75}>
                        {PURPOSES.map(p => {
                            const cur = sizes[p.key] || p.defaults;
                            return (
                                <Box key={p.key} sx={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', gap: 1.5 }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>{p.help}</Typography>
                                    </Box>
                                    <TextField
                                        type="number" size="small"
                                        value={cur.width ?? ''}
                                        onChange={(e) => setSize(p.key, 'width', e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                        sx={{ backgroundColor: '#fff' }}
                                    />
                                    {v2 && (
                                        <TextField
                                            type="number" size="small"
                                            value={cur.height ?? ''}
                                            onChange={(e) => setSize(p.key, 'height', e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            sx={{ backgroundColor: '#fff' }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                <Box sx={{ mt: 1.5 }}>
                    <PdiRow
                        label={tr('cfg_sync_imgproc_bg_fill', 'Background fill color')}
                        help={tr('cfg_sync_imgproc_bg_fill_help', "Used to pad images that don't fit the target aspect ratio. White blends most cleanly with white-background product photos.")}
                        control={
                            <Box component="label" sx={{
                                position: 'relative',
                                display: 'block',
                                width: 36, height: 36,
                                borderRadius: 1,
                                border: '1px solid rgba(0,0,0,0.18)',
                                backgroundColor: value.backgroundFillColor || '#ffffff',
                                cursor: 'pointer',
                            }}>
                                <input
                                    type="color"
                                    value={value.backgroundFillColor || '#ffffff'}
                                    onChange={(e) => onChange({ backgroundFillColor: e.target.value })}
                                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                />
                            </Box>
                        }
                    />
                </Box>
            </Box>
        </ConfigCard>
    );
};
ImageProcessingCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// Helpers used inside the cards
// ─────────────────────────────────────────────────────────────────────────

// Small select with an UPPER-CASE label above. `options` are {value,label}.
const UpperLabelSelect = ({ label, value, options, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
            {label}
        </Typography>
        <TextField
            select size="small" fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{ backgroundColor: '#fff', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' } }}
        >
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
    </Box>
);
UpperLabelSelect.propTypes = { label: PropTypes.string, value: PropTypes.string, options: PropTypes.array, onChange: PropTypes.func };

export default SyncSettingsTab;
